
//#region import libraries
import React, { useState, useEffect, useMemo } from "react"
//bootstrap
import { Col, Row } from "react-bootstrap";
//moment
import moment from "moment";
//icons
import { BsClipboard, BsClipboardCheck, BsAirplane } from "react-icons/bs";
//tremor
import { Title as TremorTitle, Subtitle, DateRangePicker, Toggle, ToggleItem } from '@tremor/react';
//custom component
import { Card, SearchBar } from "_components/atoms";
import { ClickableTable } from "_components/organisms";
import { TableHeader, TableValue } from "_components/molecules";
import Pagination from "../booking-requests/pagination";
//redux
import { useDispatch, useSelector } from "react-redux"
import { selectUserDb } from "_redux/slices/main-screen/user";
import { getSitaMessages, selectSitaMessagesData, selectSitaMessagesStatus } from "_redux/slices/assistance-bookings/bookings-history";

//styles
import { PRM_DARK_BLUE } from "_styles/colors";
//utils
import { getDateOneWeekAgo, processName } from "_utils/functions";
import { useDebounce } from '_utils/hooks';

//#endregion

//#region SITA message parsing + formatting

// Common PRM/mobility SSR codes shown as tooltips on the badges.
const SSR_LABELS = {
    WCHR: "Wheelchair – can walk short distances (ramp)",
    WCHS: "Wheelchair – cannot manage stairs (steps)",
    WCHC: "Wheelchair – completely immobile (cabin seat)",
    WCOB: "Wheelchair – on-board aircraft chair",
    WCBD: "Wheelchair – dry-cell battery powered",
    WCBW: "Wheelchair – wet-cell battery powered",
    WCLB: "Wheelchair – lithium battery powered",
    WCMP: "Wheelchair – manual power",
    BLND: "Blind / visually impaired",
    DEAF: "Deaf / hearing impaired",
    DPNA: "Disabled passenger needing assistance (intellectual/developmental)",
    MAAS: "Meet and assist",
    STCR: "Stretcher passenger",
};

const MESSAGE_TYPE_LABELS = {
    PAL: "Passenger Advice List",
    CAL: "Change Advice List",
    PSM: "Passenger Service Message",
    CSM: "Continuation Service Message",
};

// Parse a connection token e.g. "AA3129I23MCO" -> { flight, cls, day, station }
const parseSitaConnection = (value) => {
    const m = value?.match(/^([A-Z]{2}\d+)([A-Z])?(\d{2})([A-Z]{3})$/);
    if (m) return { flight: m[1], cls: m[2] || null, day: m[3], station: m[4], raw: value };
    return { raw: value };
};

// Tolerant parser for SITA PAL/CAL passenger messages.
// Returns null if the string does not look like a SITA message.
const parseSitaMessage = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    const tokens = raw.trim().split(/\s+/).filter(Boolean);
    if (tokens.length < 4) return null;

    let i = 0;
    const priority = tokens[i++];

    const addresses = [];
    while (i < tokens.length && !/^\d{6}$/.test(tokens[i])) {
        addresses.push(tokens[i++]);
    }
    const timestamp = /^\d{6}$/.test(tokens[i]) ? tokens[i++] : null;
    const type = tokens[i++] || null;

    let flight = null, flightDate = null, station = null;
    const fm = tokens[i] && tokens[i].match(/^([A-Z0-9]+)\/(\d{2}[A-Z]{3})$/);
    if (fm) { flight = fm[1]; flightDate = fm[2]; i++; }
    if (tokens[i] && /^[A-Z]{3}$/.test(tokens[i])) { station = tokens[i++]; }

    const segments = [];
    let currentSegment = null;
    let currentPax = null;
    let sentTo = null;
    let parts = [];

    const flushPax = () => {
        if (currentPax && currentSegment) currentSegment.passengers.push(currentPax);
        currentPax = null;
    };
    const ensureSegment = () => {
        if (!currentSegment) {
            currentSegment = { point: null, cls: null, passengers: [] };
            segments.push(currentSegment);
        }
    };

    for (; i < tokens.length; i++) {
        const t = tokens[i];

        if (/^PART\d+$/.test(t)) { flushPax(); parts.push(t); continue; }
        if (/^END/.test(t)) { flushPax(); break; }
        if (t === "SENT-TO") { flushPax(); sentTo = tokens[i + 1] || null; break; }

        if (t.startsWith("-")) {
            flushPax();
            const point = t.slice(1);
            const cls = (tokens[i + 1] && /^[A-Z]$/.test(tokens[i + 1])) ? tokens[++i] : null;
            currentSegment = { point, cls, passengers: [] };
            segments.push(currentSegment);
            continue;
        }

        if (t === "NIL") continue;

        // Remark: .R/ (SSR), .I/ (inbound), .O/ (onward)
        if (/^\.[A-Z]\//.test(t)) {
            if (!currentPax) continue;
            const kind = t[1];
            const val = t.slice(3);
            if (kind === "R") currentPax.ssrs.push(val);
            else if (kind === "I") currentPax.inbound = parseSitaConnection(val);
            else if (kind === "O") currentPax.onward = parseSitaConnection(val);
            else currentPax.other.push(`${kind}/${val}`);
            continue;
        }

        // Passenger record: leading party count e.g. "1ODONNELL/DIANA"
        const pm = t.match(/^(\d+)(.+)$/);
        if (pm) {
            flushPax();
            ensureSegment();
            currentPax = { count: Number(pm[1]), name: pm[2], ssrs: [], other: [], inbound: null, onward: null };
            continue;
        }

        // Continuation of a multi-word name
        if (currentPax) currentPax.name = `${currentPax.name} ${t}`.trim();
    }

    const hasPax = segments.some((s) => s.passengers.length);
    if (!hasPax) return null;

    return { priority, addresses, timestamp, type, flight, flightDate, station, parts, segments, sentTo, raw };
};

const formatSitaTimestamp = (ts) => {
    if (!ts || ts.length !== 6) return ts || "";
    return `Day ${ts.slice(0, 2)} · ${ts.slice(2, 4)}:${ts.slice(4, 6)} UTC`;
};

const formatSitaConnection = (c) => {
    if (!c) return "";
    if (!c.flight) return c.raw;
    return `${c.flight}${c.cls ? ` (${c.cls})` : ""} · day ${c.day} · ${c.station}`;
};

// Styles for the formatted view
const sitaStyles = {
    metaGrid: { display: "flex", flexWrap: "wrap", gap: "6px 18px", fontSize: 12, color: "#555", marginBottom: 10 },
    metaLabel: { color: "#999", marginRight: 4 },
    segBox: { border: "1px solid #e3e8ef", borderRadius: 8, marginBottom: 10, overflow: "hidden" },
    segHeader: { display: "flex", alignItems: "center", background: "#EAF4FB", color: PRM_DARK_BLUE, fontWeight: 600, fontSize: 13, padding: "6px 10px" },
    paxRow: { padding: "8px 10px", borderTop: "1px solid #f0f2f5" },
    paxName: { fontWeight: 600, fontSize: 13 },
    ssrBadge: { background: "#e6efff", color: PRM_DARK_BLUE, borderRadius: 4, padding: "1px 6px", fontSize: 11, fontWeight: 600, cursor: "help" },
    connLine: { fontSize: 11, color: "#666", marginTop: 3 },
    emptySeg: { fontSize: 12, color: "#aaa", padding: "6px 10px", fontStyle: "italic" },
    rawPre: { whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", fontSize: 12, color: "#444", margin: 0 },
};

const SitaMessageView = ({ content }) => {
    const parsed = useMemo(() => {
        try { return parseSitaMessage(content); } catch (e) { return null; }
    }, [content]);

    // Fall back to raw text if the message is not a recognised SITA format
    if (!parsed) {
        return <pre style={sitaStyles.rawPre}>{content}</pre>;
    }

    return (
        <div>
            <div style={sitaStyles.metaGrid}>
                {parsed.type && (
                    <span>
                        <span style={sitaStyles.metaLabel}>Type</span>
                        <b>{parsed.type}</b>{MESSAGE_TYPE_LABELS[parsed.type] ? ` – ${MESSAGE_TYPE_LABELS[parsed.type]}` : ""}
                    </span>
                )}
                {parsed.flight && (
                    <span>
                        <span style={sitaStyles.metaLabel}>Flight</span>
                        <b>{parsed.flight}</b>{parsed.flightDate ? ` / ${parsed.flightDate}` : ""}{parsed.station ? ` · ${parsed.station}` : ""}
                    </span>
                )}
                {parsed.timestamp && (
                    <span><span style={sitaStyles.metaLabel}>Received</span>{formatSitaTimestamp(parsed.timestamp)}</span>
                )}
                {parsed.sentTo && (
                    <span><span style={sitaStyles.metaLabel}>Sent to</span>{parsed.sentTo}</span>
                )}
            </div>

            {parsed.segments.map((seg, si) => (
                <div key={si} style={sitaStyles.segBox}>
                    <div style={sitaStyles.segHeader}>
                        <BsAirplane style={{ marginRight: 6 }} />
                        {seg.point || "—"}{seg.cls ? ` · Class ${seg.cls}` : ""}
                        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 500, color: "#7a8aa0" }}>
                            {seg.passengers.length ? `${seg.passengers.length} pax` : "NIL"}
                        </span>
                    </div>
                    {seg.passengers.length === 0 ? (
                        <div style={sitaStyles.emptySeg}>No PRM passengers</div>
                    ) : (
                        seg.passengers.map((pax, xi) => (
                            <div key={xi} style={sitaStyles.paxRow}>
                                <div style={sitaStyles.paxName}>
                                    {pax.count > 1 ? `${pax.count}× ` : ""}{pax.name}
                                </div>
                                {pax.ssrs.length > 0 && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                        {pax.ssrs.map((s, k) => (
                                            <span key={k} style={sitaStyles.ssrBadge} title={SSR_LABELS[s] || s}>{s}</span>
                                        ))}
                                    </div>
                                )}
                                {pax.inbound && (
                                    <div style={sitaStyles.connLine}>Inbound: {formatSitaConnection(pax.inbound)}</div>
                                )}
                                {pax.onward && (
                                    <div style={sitaStyles.connLine}>Onward: {formatSitaConnection(pax.onward)}</div>
                                )}
                                {pax.other.length > 0 && (
                                    <div style={sitaStyles.connLine}>{pax.other.join(" · ")}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            ))}
        </div>
    );
};

//#endregion

const SitaMessagesTable = () => {
    //#region redux
    const dispatch = useDispatch()
    const userDb = useSelector(selectUserDb);
    const sitaMessagesData = useSelector(selectSitaMessagesData);
    const sitaMessagesStatus = useSelector(selectSitaMessagesStatus);
    //#endregion

    //#region state
    const [date, setDate] = useState([getDateOneWeekAgo(new Date()), new Date()]);
    const [skip, setSkip] = useState(0);
    const [limit, setLimit] = useState(50);
    const [messageType, setMessageType] = useState("arrival")
    const [searchValue, setSearchValue] = useState("");
    const [messageContent, setMessageContent] = useState({ content: null, paxList: [], sender: null });
    const [selectedRowID, setSelectedRowID] = useState(null)
    const [airport, setAirport] = useState("");
    const [copied, setCopied] = useState(false);
    const debouncedValue = useDebounce(searchValue, 400);
    //#endregion

    //#region copy
    const handleCopyContent = async () => {
        if (!messageContent.content) return;
        try {
            await navigator.clipboard.writeText(messageContent.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            // Fallback for browsers/contexts without the async clipboard API
            const ta = document.createElement("textarea");
            ta.value = messageContent.content;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { }
            document.body.removeChild(ta);
        }
    };
    //#endregion

    //#region useEffect

    useEffect(() => {
        if (!date[0] || !date[1]) return;
        const len = (airport ?? '').trim().length;
        if (len === 0 || len === 3) {
            handleGetSitaMessages();
        }
    }, [debouncedValue, messageType, date, limit, skip, airport]);


    //#endregion 
    const processNameLocal = (name) => {
        return name?.replace("_", " ")
    }

    const handleClick = (index) => {
        const row = sitaMessagesData?.rows[index];
        if (!row) return;
        setSelectedRowID(row._id)
        const formattedList = row?.passengers.map((m) => ({
            ...m,
            name: processNameLocal(m.name),
        }));
        setMessageContent({ paxList: formattedList, content: row.content, sender: row.sender });
    };


    const columns = useMemo(
        () => [

            {
                Header: <TableHeader title={'Flight Code'} />,
                accessor: "flightDetails.flightCode",
                Cell: ({ cell: { value } }) => <p>{value}</p>
            },
            {
                Header: <TableHeader title={'Origin'} />,
                accessor: "flightDetails.origin",
                Cell: ({ cell: { value } }) => <TableValue title={(value)} />
            },
            {
                Header: <TableHeader title={'Destination'} />,
                accessor: "flightDetails.destination",
                Cell: ({ cell: { value } }) => <TableValue title={value} />
            },
            {
                Header: <TableHeader title={'Departure Date'} />,
                accessor: "flightDetails.departureDate",
                Cell: ({ cell: { value } }) => <TableValue title={moment(value).format('YYYY-MM-DD')} />
            },
            {
                Header: <TableHeader title={'Create Date'} />,
                accessor: "timestamp",
                Cell: ({ cell: { value } }) => <TableValue title={moment(value).format('YYYY-MM-DD HH:mm')} />
            }
        ],
        []
    );

    //#region functions
    const handleGetSitaMessages = () => {
        const data = {
            iata: userDb.details.authority,
            type: messageType,
            start: moment(new Date(date[0])).format().split("T")[0],
            end: moment(new Date(date[1])).format().split("T")[0],
            limit,
            skip,
            searchValue: debouncedValue,
            airport
        }
        dispatch(getSitaMessages(data))
    }

    const handleSearch = (val, target) => {
        const v = typeof val === 'string' ? val : val?.target?.value ?? '';

        if (target === "AIRPORT") {
            // Keep only letters, uppercase, clamp to 3
            const clean = v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
            setAirport(clean);
            setSkip(0);
        } else {
            setSearchValue(v);
            setSkip(0);
        }
    };


    //skip next rows (limit 10,25,50...)
    const showMore = () => {
        setSkip(skip + limit)
    }
    //skip to previous rows (limit 10,25,50...)
    const showLess = () => {
        setSkip(skip - limit)
    }
    //chnage number of rows per page
    const handleLimitChange = (event) => {
        const newLimit = Number(event.target.value);
        setLimit(newLimit);
    }


    //#endregion

    //#region rendering

    return (<>
        {/*TITLE*/}
        <Row className="mt-2">
            <Col md={8}>

                <TremorTitle>Sita Messages</TremorTitle>
                <Subtitle>{'View list of sita message for booking requests'}</Subtitle>

            </Col>
        </Row>
        {/* FILTERS(DATE PICKER/SEARCH/MESSAGE TYPE/AIRPORT) */}
        <Row className="mt-2">
            <Col md={2}>
                <Toggle
                    color="zinc"
                    defaultValue={messageType}
                    onValueChange={(value) => setMessageType(value)}
                >
                    <ToggleItem value={"arrival"} text="Arrival" />
                    <ToggleItem value={"departure"} text="Departure" />

                </Toggle>

            </Col>
            <Col md={4}>
                <DateRangePicker
                    onValueChange={(value) => setDate(value)}
                    value={date}
                    enableDropdown={true}
                    maxWidth="max-w-lg"
                    dropdownPlaceholder={"Last 7 Days"}
                />
            </Col>

            <Col md={2}>
                <SearchBar onChange={(val) => handleSearch(val.target.value, "AIRPORT")} maxLength={3} placeholder={"Enter iata code"} value={airport} isTimeline={false} />

            </Col>
            <Col md={4}>
                <SearchBar onChange={(val) => handleSearch(val.target.value, "SEARCH")} placeholder={"Search flight/passengers"} value={searchValue} isTimeline={false} />

            </Col>

        </Row>
        {/*TABLE + Content detail*/}
        <Row className="mt-4">
            <Col md={8}>
                <Card>
                    <div style={{ overflowY: 'scroll', height: '40em', }}>
                        {
                            <ClickableTable
                                columns={columns}
                                data={sitaMessagesData.rows?.length ? sitaMessagesData.rows : []}
                                status={sitaMessagesStatus}
                                clickable={true}
                                selectedId={selectedRowID}
                                handleClick={handleClick}
                            />
                        }
                    </div>
                </Card>
            </Col>
            {/* Message content and pax list */}
            <Col md={4}>
                <Card>
                    <div style={{ overflowY: 'scroll', height: '40em', }}>
                        {
                            !messageContent.content ? <h6>Please select a row to display its details !</h6> : <>
                                <Row className="mt-2">
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <h6 style={{ color: PRM_DARK_BLUE, marginRight: "8px", marginBottom: 0 }}>Content:</h6>
                                            <h6 style={{ fontWeight: "bold", marginBottom: 0 }}>
                                                {messageContent.sender ?? ""}
                                            </h6>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopyContent}
                                            title={copied ? "Copied" : "Copy raw message"}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 6,
                                                border: `1px solid ${copied ? "#2E7D32" : "#cfd8e3"}`,
                                                background: copied ? "#E8F5E9" : "#fff",
                                                color: copied ? "#2E7D32" : PRM_DARK_BLUE,
                                                borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                                            }}
                                        >
                                            {copied ? <BsClipboardCheck size={14} /> : <BsClipboard size={14} />}
                                            {copied ? "Copied" : "Copy"}
                                        </button>
                                    </div>
                                    <div style={{ width: "100%", marginTop: "8px" }}>
                                        <SitaMessageView content={messageContent.content} />
                                    </div>
                                </Row>

                                <Row className="mt-4 justify-content-center">
                                    <h6 style={{ color: PRM_DARK_BLUE }}>Passenger list:</h6>

                                    {messageContent.paxList.map((p, idx) => (
                                        <div key={idx} style={{ marginBottom: "15px", display: "flex" }}>
                                            {processName(p.name)}--{p.calState}

                                        </div>
                                    ))}
                                </Row>
                            </>
                        }
                    </div>
                </Card>
            </Col>
        </Row>

        {/*New simple pagination*/}
        <Row className="mt-2 justify-content-center">
            <Col xs="auto">
                <Pagination
                    showLess={showLess}
                    showMore={showMore}
                    limit={limit}
                    skippedRows={skip}
                    totalRows={sitaMessagesData.totalRows}
                    handleLimitChange={handleLimitChange}
                />
            </Col>
        </Row>

    </>
    )

    //#endregion
}

export default SitaMessagesTable;
